/**
 * ⟦A-38⟧ **La marche** — ce que coûte un abonné, en vues, au palier où tu es.
 *
 * ## Pourquoi ce module existe
 *
 * ⟦A-35⟧ a mis la montée en puissance **dans le moteur** : à 100 abonnés une vidéo produit
 * ~660 vues et un abonné s'en paie 8 ; au million elle en produit ~560 000 et un abonné en
 * coûte 224. Mais §7 point 4 du même avenant le dit sans détour : *« ce que l'interface devra
 * montrer : les points d'une vidéo, le prix d'un abonné au palier courant, et le fait que la
 * marche monte »*. À l'écran, le joueur lisait le même « 87 sur 100 » à sa première vidéo et à
 * 500 000 abonnés. Toute sa progression se passait dans des formules invisibles.
 *
 * ## La règle de ce fichier : on DEMANDE au moteur, on ne recalcule rien
 *
 * Aucune formule n'est réécrite ici. Deux fonctions du moteur, appelées avec les valeurs de
 * l'état :
 *
 * - {@link tauxConversion} — `TC = base(Qualité, Fidélité) × BonusPetiteChaîne(A) × MalusNulles`
 *   (SPEC-MOTEUR §4.6). C'est `BonusPetiteChaîne(A)` qui porte la marche, et lui seul.
 * - {@link prixAbonne} — l'inverse du TC, « le prix d'un abonné **en points** », écrite dans
 *   `formula.ts` *pour l'affichage* et qui n'avait aucun appelant.
 *
 * ⚠️ **Ce que le moteur n'expose pas**, et qu'on ne fabrique donc pas : il n'existe aucune
 * lecture `prixAbonneCourant(state)`. Le TC dépend de la **Qualité effective d'une vidéo**, qui
 * est une grandeur par vidéo, pas par chaîne. On prend donc la Qualité effective de la
 * **dernière vidéo publiée** — la seule mesure de métier que la chaîne possède réellement, figée
 * par le moteur à la publication. C'est une approximation **assumée et documentée**, pas un
 * calcul maison : elle répond à « au niveau où tu travailles aujourd'hui, un abonné coûte ça ».
 *
 * ⟦A-10⟧ **État neutre** : aucune vidéo publiée ⇒ `null` ⇒ aucune surface ne rend un pixel.
 */

import {
  PALIERS,
  prixAbonne,
  tauxConversion,
  type GameState,
  type VideoRecord,
} from "@maxime/shared";

/**
 * Le prix qu'une vidéo a **réellement** payé, en vues par abonné : lecture exacte du
 * `taux_conversion` figé dans le `VideoRecord` à la publication (SPEC-MOTEUR §4.6). Rien n'est
 * recalculé — c'est ce chiffre-là, et pas un autre, que les deux versements utilisent.
 *
 * ⚠️ Il porte tout ce que la vidéo portait : le poids volatil d'un short à Fidélité < 50 double
 * ce prix. C'est exact, et c'est pour ça qu'il appartient à la fiche d'UNE vidéo, pas au HUD.
 */
export function prixDeLaVideo(video: VideoRecord): number {
  return prixAbonne(video.taux_conversion);
}

/** Les nulles qui comptent — même sélecteur que `publish.ts` (§4.6) : publiques, classées nulle. */
function nullesVisibles(state: GameState): number {
  return state.videos.filter((v) => v.class === "nulle" && v.visibility === "public").length;
}

/**
 * Le repère du « avant » : le **premier palier du jeu** (`PALIERS[0]`, 100 abonnés), pas zéro.
 *
 * ⚠️ Zéro serait trompeur : sous 150 abonnés, `BONUS_DEBUTANT` ⟦A-9⟧ écrase le prix à ~3 vues
 * pour tenir la promesse de l'onboarding (« 100 abonnés quand le programme se termine »). C'est
 * une rampe de démarrage, pas la courbe — la comparer à la suite gonflerait la marche d'un
 * facteur qui ne raconte rien du jeu.
 */
const REPERE = PALIERS[0];

export interface Marche {
  /** Vues qu'un abonné coûte **au palier courant**, au niveau de métier de la dernière vidéo. */
  prix: number;
  /** Le palier de référence en abonnés — pour que l'écran puisse le nommer. */
  repere: number;
  /**
   * Ce que le **même travail** coûtait à {@link REPERE} abonnés : seul le nombre d'abonnés
   * change entre les deux appels, tout le reste est tenu constant. C'est la méthode de mesure
   * d'⟦A-35 §4⟧ — faire varier `A` et rien d'autre — et c'est la seule façon honnête de dire
   * « la marche a changé de hauteur » sans comparer deux choses différentes.
   */
  prixAuRepere: number;
}

/**
 * La marche au palier courant, ou `null` tant qu'aucune vidéo n'a été publiée.
 *
 * Ne rend rien non plus si le moteur répond `Infinity` (conversion nulle) : un « ∞ » dans le HUD
 * n'apprend rien à personne.
 */
export function marche(state: GameState): Marche | null {
  const derniere = state.videos[state.videos.length - 1];
  if (!derniere) return null;

  const nulles = nullesVisibles(state);
  const prix = prixAbonne(
    tauxConversion(derniere.qualite_eff, state.loyalty, state.subscribers, nulles),
  );
  const prixAuRepere = prixAbonne(
    tauxConversion(derniere.qualite_eff, state.loyalty, REPERE, nulles),
  );

  if (!Number.isFinite(prix) || !Number.isFinite(prixAuRepere)) return null;
  return { prix, repere: REPERE, prixAuRepere };
}
